import React, { memo, useState, useEffect } from "react";
import { toast } from "react-toastify";
import api from "../../../../utils/api";
import { getCurrentEnvironment } from "Utils/constants";
import { otaLogger } from "../../logger";
import {
  WizardContainer,
  WizardHeader,
  WizardTitle,
  WizardProgress,
  ProgressBar,
  ProgressFill,
  StepIndicator,
  StepContainer,
  StepTitle,
  StepDescription,
  FormGroup,
  FormLabel,
  Select,
  Button,
  ButtonGroup,
  Table,
  TableHeader,
  TableRow,
  TableCell,
  Checkbox,
  StrategyCard,
  StrategyGrid,
  StrategyTitle,
  StrategyDescription,
  SummarySection,
  SummaryItem,
  SummaryLabel,
  SummaryValue,
  AlertBox,
  LoadingSpinner,
} from "./styles-wizard";

const log = otaLogger.byPrefix("CloudDeploymentWizard");

interface ReleaseComponent {
  componentName: string;
  awsComponentName: string;
  versionNumber: string;
}

const BASELINE_COMPONENTS: {
  name: string;
  awsName: string;
  version: string;
}[] = [
  {
    name: "Greengrass Nucleus",
    awsName: "aws.greengrass.Nucleus",
    version: "2.16.1",
  },
  {
    name: "Greengrass CLI",
    awsName: "aws.greengrass.Cli",
    version: "2.16.1",
  },
  {
    name: "Log Manager",
    awsName: "aws.greengrass.LogManager",
    version: "2.3.11",
  },
  {
    name: "Vehicle Telemetry",
    awsName: "com.cyngn.VehicleTelemetry",
    version: "1.0.6",
  },
];

interface WizardState {
  currentStep: number;
  fleetId: string;
  fleetName: string;
  vehicleTypeId: string;
  vehicleTypeName: string;
  releaseId: string;
  releaseName: string;
  releaseComponents: ReleaseComponent[];
  deploymentStrategy: "ALL" | "SPECIFIC";
  selectedVehicleIds: string[];
}

interface Fleet {
  id: string;
  name: string;
  description: string;
}

interface VehicleType {
  id: string;
  name: string;
}

interface Release {
  id: string;
  name: string;
  vehicleTypeName: string;
  status: string;
  orchestratorVersion: string;
  orchestratorArn: string;
}

interface Vehicle {
  id: string;
  vin: string;
  awsThingName: string;
  vehicleType: string;
  vehicleTypeId: string;
  vehicleTypeName: string;
  currentRelease: string | null;
  currentReleaseName: string | null;
  connectionStatus: string;
}

const TOTAL_STEPS = 5;

interface CloudDeploymentWizardProps {
  onDeploySuccess?: () => void;
}

export const CloudDeploymentWizard = memo(
  ({ onDeploySuccess }: CloudDeploymentWizardProps) => {
    const [wizardState, setWizardState] = useState<WizardState>({
      currentStep: 1,
      fleetId: "",
      fleetName: "",
      vehicleTypeId: "",
      vehicleTypeName: "",
      releaseId: "",
      releaseName: "",
      releaseComponents: [],
      deploymentStrategy: "ALL",
      selectedVehicleIds: [],
    });

    const [fleets, setFleets] = useState<Fleet[]>([]);
    const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
    const [releases, setReleases] = useState<Release[]>([]);
    const [vehicles, setVehicles] = useState<Vehicle[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [deploying, setDeploying] = useState(false);
    const [showSameReleaseModal, setShowSameReleaseModal] = useState(false);

    useEffect(() => {
      loadFleets();
    }, []);

    const loadFleets = async () => {
      try {
        setLoading(true);
        const response = await api.get("/ota/fleets/");
        const fleetsData = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        setFleets(fleetsData);
        setError(null);
      } catch (err: unknown) {
        const error = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        setError(
          "Failed to load fleets: " +
            (error.response?.data?.message || error.message || "Unknown error")
        );
      } finally {
        setLoading(false);
      }
    };

    const loadReleases = async (vehicleTypeId: string) => {
      try {
        setLoading(true);
        const response = await api.get(
          `/ota/releases/?vehicle_type=${vehicleTypeId}&status=ready`
        );
        const releasesData = Array.isArray(response.data)
          ? response.data
          : response.data?.results || [];
        setReleases(releasesData);
        setError(null);
      } catch (err: unknown) {
        const error = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        setError(
          "Failed to load releases: " +
            (error.response?.data?.message || error.message || "Unknown error")
        );
      } finally {
        setLoading(false);
      }
    };

    const handleFleetSelect = (fleetId: string) => {
      const selectedFleet = fleets.find((f) => f.id === fleetId);
      if (!selectedFleet) return;

      setWizardState((prev) => ({
        ...prev,
        fleetId,
        fleetName: selectedFleet.name,
        vehicleTypeId: "",
        vehicleTypeName: "",
        releaseId: "",
        releaseName: "",
      }));
    };

    const handleVehicleTypeSelect = (vehicleTypeId: string) => {
      const selectedType = vehicleTypes.find((vt) => vt.id === vehicleTypeId);
      if (!selectedType) return;

      setWizardState((prev) => ({
        ...prev,
        vehicleTypeId,
        vehicleTypeName: selectedType.name,
        releaseId: "",
        releaseName: "",
      }));
    };

    const handleReleaseSelect = async (releaseId: string) => {
      const selectedRelease = releases.find((r) => r.id === releaseId);
      if (!selectedRelease) return;

      setWizardState((prev) => ({
        ...prev,
        releaseId,
        releaseName: selectedRelease.name,
        releaseComponents: [],
      }));

      try {
        const detail = await api.get(`/ota/releases/${releaseId}/`);
        const components: ReleaseComponent[] = detail.data?.components ?? [];
        setWizardState((prev) => ({ ...prev, releaseComponents: components }));
      } catch {
        // non-critical — summary will just show no components
      }
    };

    const handleStrategySelect = (strategy: "ALL" | "SPECIFIC") => {
      setWizardState((prev) => ({
        ...prev,
        deploymentStrategy: strategy,
        selectedVehicleIds: strategy === "ALL" ? [] : prev.selectedVehicleIds,
      }));
    };

    const handleVehicleToggle = (vehicleId: string) => {
      setWizardState((prev) => {
        const isSelected = prev.selectedVehicleIds.includes(vehicleId);
        return {
          ...prev,
          selectedVehicleIds: isSelected
            ? prev.selectedVehicleIds.filter((id) => id !== vehicleId)
            : [...prev.selectedVehicleIds, vehicleId],
        };
      });
    };

    const goToStep = (step: number) => {
      setWizardState((prev) => ({ ...prev, currentStep: step }));
    };

    // Step 1 → 2: fetch unique vehicle types from fleet
    const goToStep2 = async () => {
      if (!wizardState.fleetId) {
        setError("Please select a fleet");
        return;
      }

      try {
        setLoading(true);
        const env = getCurrentEnvironment();
        const envParam = env ? `&environment=${encodeURIComponent(env)}` : "";
        const vehiclesResponse = await api.get(
          `/ota/vehicles/?fleet_id=${wizardState.fleetId}${envParam}`
        );

        const raw = vehiclesResponse.data;
        const fleetVehicles: Vehicle[] = Array.isArray(raw)
          ? raw
          : raw?.results || [];

        if (fleetVehicles.length === 0) {
          setError("Selected fleet has no vehicles");
          return;
        }

        // Extract unique vehicle types from fleet vehicles
        const seen = new Set<string>();
        const uniqueTypes: VehicleType[] = [];
        for (const v of fleetVehicles) {
          const typeId: string = v.vehicleType ?? v.vehicleTypeId ?? "";
          const typeName: string = v.vehicleTypeName ?? "";
          if (typeId && !seen.has(typeId)) {
            seen.add(typeId);
            uniqueTypes.push({ id: typeId, name: typeName });
          }
        }

        if (uniqueTypes.length === 0) {
          setError("No vehicle types found in fleet.");
          return;
        }

        setVehicleTypes(uniqueTypes);
        setError(null);
        goToStep(2);
      } catch (err: unknown) {
        const error = err as {
          response?: { data?: { message?: string }; status?: number };
          message?: string;
        };
        log.error("goToStep2 error", err as Record<string, any>);
        setError(
          "Failed to load vehicle types: " +
            (error.response?.data?.message || error.message || "Unknown error")
        );
      } finally {
        setLoading(false);
      }
    };

    // Step 2 → 3: load releases for selected vehicle type
    const goToStep3 = async () => {
      if (!wizardState.vehicleTypeId) {
        setError("Please select a vehicle type");
        return;
      }
      await loadReleases(wizardState.vehicleTypeId);
      goToStep(3);
      setError(null);
    };

    // Step 3 → 4: load vehicles of selected vehicle type in fleet
    const goToStep4 = async () => {
      if (!wizardState.releaseId) {
        setError("Please select a release");
        return;
      }

      try {
        setLoading(true);
        const env2 = getCurrentEnvironment();
        const envParam2 = env2
          ? `&environment=${encodeURIComponent(env2)}`
          : "";
        const vehiclesResponse = await api.get(
          `/ota/vehicles/?fleet_id=${wizardState.fleetId}&vehicle_type_id=${wizardState.vehicleTypeId}${envParam2}`
        );
        const vehiclesData: Vehicle[] = Array.isArray(vehiclesResponse.data)
          ? vehiclesResponse.data
          : vehiclesResponse.data?.results || [];
        setVehicles(vehiclesData);
      } catch (err: unknown) {
        const error = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        setError(
          "Failed to load vehicles: " +
            (error.response?.data?.message || error.message || "Unknown error")
        );
      } finally {
        setLoading(false);
      }

      goToStep(4);
      setError(null);
    };

    // Step 4 → 5
    const goToStep5 = () => {
      if (
        wizardState.deploymentStrategy === "SPECIFIC" &&
        wizardState.selectedVehicleIds.length === 0
      ) {
        setError("Please select at least one vehicle");
        return;
      }

      goToStep(5);
      setError(null);
    };

    const executeDeployment = async () => {
      setDeploying(true);
      setError(null);

      try {
        const currentEnv = getCurrentEnvironment();
        const payload = {
          fleet_id: wizardState.fleetId,
          release_id: wizardState.releaseId,
          deployment_strategy: wizardState.deploymentStrategy,
          ...(wizardState.deploymentStrategy === "SPECIFIC" && {
            vehicle_ids: wizardState.selectedVehicleIds,
          }),
          ...(currentEnv && { environment: currentEnv }),
        };

        const response = await api.post(
          "/ota/releases/create-cloud-deployment/",
          payload
        );

        if (response.data.status === "success") {
          const deploymentData = response.data.data;
          toast.success(
            `Deployment executed successfully. Session ID: ${deploymentData.id} — ${deploymentData.deploymentsCreated} deployment(s) created.`
          );
          if (response.data.same_release_warning) {
            toast.info(response.data.same_release_warning, { autoClose: 8000 });
          }
          onDeploySuccess?.();

          setWizardState({
            currentStep: 1,
            fleetId: "",
            fleetName: "",
            vehicleTypeId: "",
            vehicleTypeName: "",
            releaseId: "",
            releaseName: "",
            releaseComponents: [],
            deploymentStrategy: "ALL",
            selectedVehicleIds: [],
          });
          await loadFleets();
        }
      } catch (err: unknown) {
        const error = err as {
          response?: { data?: { message?: string } };
          message?: string;
        };
        const errorMsg =
          error.response?.data?.message || error.message || "Unknown error";
        setError(`Deployment failed: ${errorMsg}`);
        toast.error(`Deployment failed: ${errorMsg}`);
      } finally {
        setDeploying(false);
      }
    };

    // The full set of vehicles this deployment will actually target, regardless
    // of strategy — used to outline the vehicle list on the review step (DEV-4972).
    const targetVehicles =
      wizardState.currentStep >= 4
        ? wizardState.deploymentStrategy === "ALL"
          ? vehicles
          : vehicles.filter((v) =>
              wizardState.selectedVehicleIds.includes(v.id)
            )
        : [];

    // Vehicles in the current target set that already have this release installed.
    const sameReleaseVehicles =
      wizardState.currentStep >= 4
        ? vehicles.filter((v) => {
            if (v.currentRelease !== wizardState.releaseId) return false;
            if (wizardState.deploymentStrategy === "ALL") return true;
            return wizardState.selectedVehicleIds.includes(v.id);
          })
        : [];

    const handleExecuteClick = () => {
      if (sameReleaseVehicles.length > 0) {
        setShowSameReleaseModal(true);
      } else {
        executeDeployment();
      }
    };

    const progressPercentage = (wizardState.currentStep / TOTAL_STEPS) * 100;

    return (
      <WizardContainer>
        <WizardHeader>
          <WizardTitle>Cloud Deployment Wizard</WizardTitle>
          <StepIndicator>
            Step {wizardState.currentStep} of {TOTAL_STEPS}
          </StepIndicator>
        </WizardHeader>

        <WizardProgress>
          <ProgressBar>
            <ProgressFill style={{ width: `${progressPercentage}%` }} />
          </ProgressBar>
        </WizardProgress>

        {error && <AlertBox variant="error">{error}</AlertBox>}

        {/* Step 1: Select Fleet */}
        {wizardState.currentStep === 1 && (
          <StepContainer>
            <StepTitle>Step 1: Select Fleet</StepTitle>
            <StepDescription>
              Choose the fleet you want to deploy to
            </StepDescription>

            <FormGroup>
              <FormLabel>Fleet *</FormLabel>
              <Select
                value={wizardState.fleetId}
                onChange={(e) => handleFleetSelect(e.target.value)}
                disabled={loading}
              >
                <option value="">Select fleet...</option>
                {fleets.map((fleet) => (
                  <option key={fleet.id} value={fleet.id}>
                    {fleet.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <ButtonGroup>
              <Button
                onClick={goToStep2}
                disabled={!wizardState.fleetId || loading}
              >
                {loading ? "Loading..." : "Next →"}
              </Button>
            </ButtonGroup>
          </StepContainer>
        )}

        {/* Step 2: Select Vehicle Type */}
        {wizardState.currentStep === 2 && (
          <StepContainer>
            <StepTitle>Step 2: Select Vehicle Type</StepTitle>
            <StepDescription>
              Choose the vehicle type to deploy to within{" "}
              {wizardState.fleetName}
            </StepDescription>

            <FormGroup>
              <FormLabel>Vehicle Type *</FormLabel>
              <Select
                value={wizardState.vehicleTypeId}
                onChange={(e) => handleVehicleTypeSelect(e.target.value)}
                disabled={loading}
              >
                <option value="">Select vehicle type...</option>
                {vehicleTypes.map((vt) => (
                  <option key={vt.id} value={vt.id}>
                    {vt.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            <ButtonGroup>
              <Button variant="secondary" onClick={() => goToStep(1)}>
                ← Back
              </Button>
              <Button
                onClick={goToStep3}
                disabled={!wizardState.vehicleTypeId || loading}
              >
                {loading ? "Loading..." : "Next →"}
              </Button>
            </ButtonGroup>
          </StepContainer>
        )}

        {/* Step 3: Select Release */}
        {wizardState.currentStep === 3 && (
          <StepContainer>
            <StepTitle>Step 3: Select Target Release</StepTitle>
            <StepDescription>
              Choose a release for {wizardState.vehicleTypeName}
            </StepDescription>

            <FormGroup>
              <FormLabel>Release *</FormLabel>
              <Select
                value={wizardState.releaseId}
                onChange={(e) => handleReleaseSelect(e.target.value)}
                disabled={loading}
              >
                <option value="">Select release...</option>
                {releases.map((release) => (
                  <option key={release.id} value={release.id}>
                    {release.name}
                  </option>
                ))}
              </Select>
            </FormGroup>

            {releases.length === 0 && !loading && (
              <AlertBox variant="warning">
                No ready releases found for {wizardState.vehicleTypeName}.
              </AlertBox>
            )}

            <ButtonGroup>
              <Button variant="secondary" onClick={() => goToStep(2)}>
                ← Back
              </Button>
              <Button
                onClick={goToStep4}
                disabled={!wizardState.releaseId || loading}
              >
                {loading ? "Loading..." : "Next →"}
              </Button>
            </ButtonGroup>
          </StepContainer>
        )}

        {/* Step 4: Deployment Strategy */}
        {wizardState.currentStep === 4 && (
          <StepContainer>
            <StepTitle>Step 4: Deployment Strategy</StepTitle>
            <StepDescription>Choose your deployment target</StepDescription>

            {vehicles.some(
              (v) => v.currentRelease === wizardState.releaseId
            ) && (
              <AlertBox variant="warning">
                {
                  vehicles.filter(
                    (v) => v.currentRelease === wizardState.releaseId
                  ).length
                }{" "}
                vehicle(s) already have release &ldquo;{wizardState.releaseName}
                &rdquo; installed. A new orchestrator version will be created
              </AlertBox>
            )}

            <StrategyGrid>
              <StrategyCard
                selected={wizardState.deploymentStrategy === "ALL"}
                onClick={() => handleStrategySelect("ALL")}
              >
                <StrategyTitle>Deploy to All</StrategyTitle>
                <StrategyDescription>
                  Deploy individually to every {wizardState.vehicleTypeName}{" "}
                  vehicle in the fleet
                </StrategyDescription>
              </StrategyCard>

              <StrategyCard
                selected={wizardState.deploymentStrategy === "SPECIFIC"}
                onClick={() => handleStrategySelect("SPECIFIC")}
              >
                <StrategyTitle>Deploy to Specific</StrategyTitle>
                <StrategyDescription>
                  Select specific vehicles
                </StrategyDescription>
              </StrategyCard>
            </StrategyGrid>

            {wizardState.deploymentStrategy === "SPECIFIC" && (
              <>
                <StepTitle style={{ marginTop: "2rem", fontSize: "1.2rem" }}>
                  Select Vehicles
                </StepTitle>
                {vehicles.length > 0 ? (
                  <Table>
                    <thead>
                      <TableRow header>
                        <TableHeader>
                          <Checkbox
                            type="checkbox"
                            checked={
                              wizardState.selectedVehicleIds.length ===
                              vehicles.length
                            }
                            onChange={(e) => {
                              if (e.target.checked) {
                                setWizardState((prev) => ({
                                  ...prev,
                                  selectedVehicleIds: vehicles.map((v) => v.id),
                                }));
                              } else {
                                setWizardState((prev) => ({
                                  ...prev,
                                  selectedVehicleIds: [],
                                }));
                              }
                            }}
                          />
                        </TableHeader>
                        <TableHeader>Name</TableHeader>
                        <TableHeader>Current Release</TableHeader>
                        <TableHeader>Status</TableHeader>
                      </TableRow>
                    </thead>
                    <tbody>
                      {vehicles.map((vehicle) => {
                        const alreadyInstalled =
                          vehicle.currentRelease === wizardState.releaseId;
                        return (
                          <TableRow key={vehicle.id}>
                            <TableCell>
                              <Checkbox
                                type="checkbox"
                                checked={wizardState.selectedVehicleIds.includes(
                                  vehicle.id
                                )}
                                onChange={() => handleVehicleToggle(vehicle.id)}
                              />
                            </TableCell>
                            <TableCell style={{ whiteSpace: "nowrap" }}>
                              {vehicle.awsThingName || vehicle.vin}
                            </TableCell>
                            <TableCell>
                              {vehicle.currentReleaseName || "None"}
                              {alreadyInstalled && (
                                <span
                                  style={{
                                    marginLeft: 6,
                                    fontSize: 10,
                                    padding: "1px 5px",
                                    borderRadius: 4,
                                    background: "#fef3c7",
                                    color: "#92400e",
                                  }}
                                >
                                  already installed
                                </span>
                              )}
                            </TableCell>
                            <TableCell>{vehicle.connectionStatus}</TableCell>
                          </TableRow>
                        );
                      })}
                    </tbody>
                  </Table>
                ) : (
                  <AlertBox variant="warning">
                    No vehicles found for this fleet and vehicle type.
                  </AlertBox>
                )}
              </>
            )}

            <ButtonGroup>
              <Button variant="secondary" onClick={() => goToStep(3)}>
                ← Back
              </Button>
              <Button
                onClick={goToStep5}
                disabled={
                  wizardState.deploymentStrategy === "SPECIFIC" &&
                  wizardState.selectedVehicleIds.length === 0
                }
              >
                Next →
              </Button>
            </ButtonGroup>
          </StepContainer>
        )}

        {/* Step 5: Summary & Execute */}
        {wizardState.currentStep === 5 && (
          <StepContainer>
            <StepTitle>Step 5: Review & Execute</StepTitle>
            <StepDescription>
              Review your deployment configuration
            </StepDescription>

            <SummarySection>
              <SummaryItem>
                <SummaryLabel>Fleet:</SummaryLabel>
                <SummaryValue>{wizardState.fleetName}</SummaryValue>
              </SummaryItem>

              <SummaryItem>
                <SummaryLabel>Vehicle Type:</SummaryLabel>
                <SummaryValue>{wizardState.vehicleTypeName}</SummaryValue>
              </SummaryItem>

              <SummaryItem>
                <SummaryLabel>Release:</SummaryLabel>
                <SummaryValue>{wizardState.releaseName}</SummaryValue>
              </SummaryItem>

              <SummaryItem>
                <SummaryLabel>Strategy:</SummaryLabel>
                <SummaryValue>
                  {wizardState.deploymentStrategy === "ALL"
                    ? "Deploy to All Vehicles"
                    : "Deploy to Specific Vehicles"}
                </SummaryValue>
              </SummaryItem>

              <SummaryItem>
                <SummaryLabel>
                  Target Vehicles ({targetVehicles.length}):
                </SummaryLabel>
                <SummaryValue>
                  {targetVehicles.map((v) => (
                    <div
                      key={v.id}
                      style={{ color: "#888", fontSize: "0.9em" }}
                    >
                      {v.awsThingName || v.vin}
                    </div>
                  ))}
                </SummaryValue>
              </SummaryItem>

              <SummaryItem>
                <SummaryLabel>Baseline Components:</SummaryLabel>
                <SummaryValue>
                  {BASELINE_COMPONENTS.map((c) => (
                    <div
                      key={c.awsName}
                      style={{ color: "#888", fontSize: "0.9em" }}
                    >
                      {c.name} — v{c.version}
                    </div>
                  ))}
                </SummaryValue>
              </SummaryItem>

              {wizardState.releaseComponents.length > 0 && (
                <SummaryItem>
                  <SummaryLabel>Release Components:</SummaryLabel>
                  <SummaryValue>
                    {wizardState.releaseComponents.map((c) => (
                      <div key={c.awsComponentName}>
                        {c.componentName} — v{c.versionNumber}
                      </div>
                    ))}
                  </SummaryValue>
                </SummaryItem>
              )}
            </SummarySection>

            {sameReleaseVehicles.length > 0 && (
              <AlertBox variant="warning">
                {sameReleaseVehicles.length} vehicle(s) already have release
                &ldquo;
                {wizardState.releaseName}&rdquo; installed:{" "}
                {sameReleaseVehicles
                  .map((v) => v.awsThingName || v.vin)
                  .join(", ")}
                . A new orchestrator version will be created automatically
              </AlertBox>
            )}

            <ButtonGroup>
              <Button
                variant="secondary"
                onClick={() => goToStep(4)}
                disabled={deploying}
              >
                ← Back
              </Button>
              <Button
                onClick={handleExecuteClick}
                disabled={deploying}
                variant="primary"
              >
                {deploying ? "Deploying..." : "Execute Deployment"}
              </Button>
            </ButtonGroup>
          </StepContainer>
        )}

        {deploying && <LoadingSpinner>Deploying...</LoadingSpinner>}

        {showSameReleaseModal && (
          <div
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.55)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 1000,
            }}
          >
            <div
              style={{
                background: "#1e1e2e",
                border: "1px solid #f59e0b",
                borderRadius: 8,
                padding: "28px 32px",
                maxWidth: 480,
                width: "90%",
                boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
              }}
            >
              <div
                style={{
                  fontSize: 18,
                  fontWeight: 700,
                  marginBottom: 12,
                  color: "#f59e0b",
                }}
              >
                Re-deploy Same Release?
              </div>
              <p style={{ margin: "0 0 12px", lineHeight: 1.5, color: "#ccc" }}>
                {sameReleaseVehicles.length} vehicle(s) already have release{" "}
                <strong>&ldquo;{wizardState.releaseName}&rdquo;</strong>{" "}
                installed:
              </p>
              <ul
                style={{ margin: "0 0 16px", paddingLeft: 20, color: "#ccc" }}
              >
                {sameReleaseVehicles.map((v) => (
                  <li key={v.id}>{v.awsThingName || v.vin}</li>
                ))}
              </ul>
              <p style={{ margin: "0 0 24px", lineHeight: 1.5, color: "#ccc" }}>
                A new orchestrator version will be created so Greengrass
                restarts and processes the new session. Do you want to continue?
              </p>
              <div
                style={{ display: "flex", justifyContent: "flex-end", gap: 12 }}
              >
                <Button
                  variant="secondary"
                  onClick={() => setShowSameReleaseModal(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => {
                    setShowSameReleaseModal(false);
                    executeDeployment();
                  }}
                >
                  Deploy Anyway
                </Button>
              </div>
            </div>
          </div>
        )}
      </WizardContainer>
    );
  }
);
